import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from './translatable-text.model';

export const CUSTOM_PROPERTY_DEFINITION_TRANSLATABLE_FIELDS = ['label', 'sectionTitle'] as const;
export type CustomPropertyDefinitionTranslatableField = (typeof CUSTOM_PROPERTY_DEFINITION_TRANSLATABLE_FIELDS)[number];
export type CustomPropertyDefinitionTranslations = TranslatableText<CustomPropertyDefinitionTranslatableField>;

export const customPropertyTypeValues = ['text', 'number', 'boolean', 'date', 'select', 'multi-select', 'tags', 'reference'] as const;
export type CustomPropertyType = (typeof customPropertyTypeValues)[number];

export const customPropertyReferenceEntityTypeValues = ['location', 'skill'] as const;
export type CustomPropertyReferenceEntityType = (typeof customPropertyReferenceEntityTypeValues)[number];

const customPropertyTypeSchema = type("'text' | 'number' | 'boolean' | 'date' | 'select' | 'multi-select' | 'tags' | 'reference'");

export const createCustomPropertyDefinitionSchema = type({
  entityType: type('string').configure({
    description: 'Entity type the custom property attaches to (e.g., "person", "location", "skill").',
    examples: ['person', 'location'],
  }),
  key: type('string').configure({
    description: 'Stable machine-readable key for the property. Used as the data field name.',
    examples: ['employeeNumber', 'costCenter'],
  }),
  label: type('string').configure({
    description: 'Human-readable label shown in UI forms and detail views.',
    examples: ['Employee Number', 'Cost Center'],
  }),
  propertyType: customPropertyTypeSchema.configure({
    description:
      "Data type of the property: 'text' for free text, 'number' for numeric values, 'boolean' for yes/no, 'date' for ISO dates, 'select' for single choice from an option list, 'multi-select' for multiple choices from a fixed option list, 'tags' for free-form multi-value chips, 'reference' for links to another entity — single or multiple ids depending on `config.multiple`.",
  }),
  'config?': type('object | null').configure({
    description:
      'Type-specific configuration. Examples: `{ options: [{ value, label }] }` for select/multi-select, `{ referenceEntityType: "location" }` for reference, `{ referenceEntityType: "skill", multiple: true }` for multi-reference, `{ min, max }` for number. Optional and nullable.',
    examples: [
      { options: ['low', 'medium', 'high'] },
      { referenceEntityType: 'location' },
      { referenceEntityType: 'skill', multiple: true },
    ],
  }),
  'required?': type('boolean').configure({
    description: 'Whether the property is required when editing the parent entity. Defaults to false.',
    examples: [true, false],
  }),
  'filterable?': type('boolean').configure({
    description: 'Whether collection pages may expose this property as a list filter. Defaults to false.',
    examples: [true, false],
  }),
  'sortable?': type('boolean').configure({
    description: 'Whether collection pages may expose this property as a sort field. Defaults to false.',
    examples: [true, false],
  }),
  'priority?': type('number').configure({
    description: 'Display ordering priority (lower values render first). Optional.',
    examples: [10],
  }),
  'sectionTitle?': type('string | null').configure({
    description: 'Optional section grouping title in edit forms. Nullable.',
    examples: ['HR Details'],
  }),
  'sectionIcon?': type('string | null').configure({
    description: 'Optional icon name rendered next to the section title. Nullable.',
    examples: ['briefcase'],
  }),
  'translations?': translatableTextSchema(CUSTOM_PROPERTY_DEFINITION_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `label` and `sectionTitle`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({ description: 'Payload to create a new custom property definition for an entity type.' });

export type CreateCustomPropertyDefinition = typeof createCustomPropertyDefinitionSchema.t;

export const updateCustomPropertyDefinitionSchema = type({
  'label?': type('string').configure({
    description: 'Updated human-readable label. Optional.',
    examples: ['Cost Center'],
  }),
  'propertyType?': customPropertyTypeSchema.configure({
    description: 'Updated data type. Changing this may require data migration. Optional — same semantics as on creation.',
  }),
  'config?': type('object | null').configure({
    description: 'Updated type-specific configuration. Optional and nullable (pass null to clear).',
    examples: [{ options: ['low', 'medium', 'high'] }],
  }),
  'required?': type('boolean').configure({
    description: 'Updated required flag. Optional.',
    examples: [true],
  }),
  'filterable?': type('boolean').configure({
    description: 'Updated collection filterability flag. Optional.',
    examples: [true],
  }),
  'sortable?': type('boolean').configure({
    description: 'Updated collection sortability flag. Optional.',
    examples: [true],
  }),
  'priority?': type('number').configure({
    description: 'Updated display priority. Optional.',
    examples: [20],
  }),
  'sectionTitle?': type('string | null').configure({
    description: 'Updated section title. Optional and nullable (pass null to ungroup).',
    examples: ['HR Details'],
  }),
  'sectionIcon?': type('string | null').configure({
    description: 'Updated section icon name. Optional and nullable.',
    examples: ['briefcase'],
  }),
  'translations?': translatableTextSchema(CUSTOM_PROPERTY_DEFINITION_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({ description: 'Partial update payload for an existing custom property definition.' });

export type UpdateCustomPropertyDefinition = typeof updateCustomPropertyDefinitionSchema.t;

export const customPropertyDefinitionQuerySchema = type({
  'entityType?': type('string').configure({
    description: 'Filter definitions by the entity type they attach to. Optional.',
    examples: ['person'],
  }),
}).configure({ description: 'Query parameters for filtering the custom property definitions list.' });

export type CustomPropertyDefinitionQuery = typeof customPropertyDefinitionQuerySchema.t;

export const customPropertyDefinitionDetailQuerySchema = type({
  'include?': type('string').configure({
    description: 'Set to `translations` to add the full per-language translations map to the response. Comma-separated lists are accepted.',
    examples: ['translations'],
  }),
}).configure({ description: 'Query parameters for a single custom property definition.' });

export const customPropertyDistinctValuesQuerySchema = type({
  entityType: type('string').configure({
    description: 'Entity type the property belongs to, e.g. `location` or `skill`.',
    examples: ['location'],
  }),
  propertyKey: type('string').configure({
    description: 'Key of a `filterable` definition for `entityType`. Unknown or non-filterable keys return an empty array.',
    examples: ['region'],
  }),
}).configure({ description: 'Query parameters for the distinct stored values of one custom property.' });

export const customPropertyReferenceOptionsQuerySchema = type({
  entityType: type('string').configure({
    description: 'Entity type the reference property belongs to, e.g. `calendar-slot`.',
    examples: ['calendar-slot'],
  }),
  referenceEntityType: type('string').configure({
    description: 'Entity the options come from: `location` or `skill`. Any other value returns an empty array.',
    examples: ['skill'],
  }),
}).configure({ description: 'Query parameters for the selectable options of a reference custom property.' });

/**
 * Custom property definition response returned by the API.
 */
export interface CustomPropertyDefinitionResponse {
  id: string;
  entityType: string;
  key: string;
  label: string;
  propertyType: CustomPropertyType;
  config: Record<string, unknown> | null;
  required: boolean;
  filterable: boolean;
  sortable: boolean;
  priority: number;
  sectionTitle: string | null;
  sectionIcon: string | null;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}

export const customPropertyDefinitionResponseSchema = type({
  id: 'string',
  entityType: 'string',
  key: 'string',
  label: 'string',
  propertyType: customPropertyTypeSchema,
  config: 'object | null',
  required: 'boolean',
  filterable: 'boolean',
  sortable: 'boolean',
  priority: 'number',
  sectionTitle: 'string | null',
  sectionIcon: 'string | null',
  'translations?': translatableTextSchema(CUSTOM_PROPERTY_DEFINITION_TRANSLATABLE_FIELDS).configure({
    description: 'Full per-language translations. Present only when the request sent `include=translations`.',
  }),
  createdAt: 'string',
  updatedAt: 'string',
}).configure({ description: 'Custom property definition as returned by the API.' });

export const customPropertyDefinitionListResponseSchema = customPropertyDefinitionResponseSchema
  .array()
  .configure({ description: 'Custom property definitions.' });

export const customPropertyDistinctValuesResponseSchema = type('string[]').configure({
  description: 'Distinct stored values, sorted alphabetically. Tags are returned as individual strings.',
});

export const customPropertyReferenceOptionsResponseSchema = type({
  value: 'string',
  label: 'string',
  icon: 'string',
  description: 'string | null',
  color: 'string | null',
  'imageUrl?': 'string | null',
  'data?': 'object | null',
})
  .array()
  .configure({ description: 'Selectable reference options.' });
